<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted } from 'vue';

  interface SwiperProps {
    images: string[];
    modelValue: number;
  }

  const props = defineProps<SwiperProps>();

  const emit = defineEmits<{
    (e: 'update:modelValue', value: number): void;
  }>();

  const startX = ref(0);
  const startY = ref(0);
  const moveX = ref(0);
  const swipeDirection = ref<'horizontal' | 'vertical' | null>(null);
  const width = ref(0);
  const height = ref(0);
  const startedTime = ref(0);
  const directionThreshold = 8;
  const isAnimation = ref(true);
  const rootRef = ref<HTMLElement>();
  const isTouchDevice = 'ontouchstart' in window;

  const translateX = computed(() => {
    const distanceTraveled = startX.value - moveX.value;
    const translateX = width.value * props.modelValue * -1;
    return translateX - distanceTraveled;
  });

  const handleQuickSwipe = () => {
    const isLeftSwipped = moveX.value > startX.value;
    if (isLeftSwipped) {
      changeIndex(Math.max(props.modelValue - 1, 0));
    } else {
      changeIndex(Math.min(props.modelValue + 1, props.images.length - 1));
    }
  };
  const handleSlowSwipe = () => {
    const findedIndex = Math.round(Math.abs(translateX.value) / width.value);
    if (translateX.value > 0) {
      changeIndex(0);
    } else if (findedIndex >= props.images.length - 1) {
      changeIndex(props.images.length - 1);
    } else {
      changeIndex(findedIndex);
    }
  };

  const changeIndex = (index: number) => {
    emit('update:modelValue', index);
  };
  const OnTouchStart = (event: TouchEvent) => {
    isAnimation.value = false;
    startX.value = event.touches[0].clientX;
    startY.value = event.touches[0].clientY;
    moveX.value = event.touches[0].clientX;
    swipeDirection.value = null;
    startedTime.value = Date.now();
  };

  const OnTouchMove = (event: TouchEvent) => {
    const { clientX, clientY } = event.touches[0];

    if (!swipeDirection.value) {
      const deltaX = Math.abs(clientX - startX.value);
      const deltaY = Math.abs(clientY - startY.value);
      if (Math.max(deltaX, deltaY) < directionThreshold) return;
      swipeDirection.value = deltaX > deltaY ? 'horizontal' : 'vertical';
    }

    if (swipeDirection.value === 'vertical') return;

    if (event.cancelable) event.preventDefault();
    moveX.value = clientX;
  };

  const OnTouchEnd = async () => {
    isAnimation.value = true;
    const isClick = startX.value === moveX.value;
    swipeDirection.value = null;
    if (isClick) return;
    const quickMoveTime = 300;
    const passedTime = Date.now() - startedTime.value;
    const isQuickSwipe = passedTime < quickMoveTime;

    isQuickSwipe ? handleQuickSwipe() : handleSlowSwipe();

    startX.value = 0;
    moveX.value = 0;
  };

  const onMouseLeave = () => {
    if (isTouchDevice) return;
    changeIndex(0);
  };

  const onMouseEnter = (index: number) => {
    if (isTouchDevice) return;
    changeIndex(index);
  };

  const updateSizings = () => {
    width.value = rootRef.value?.offsetWidth || 0;
    height.value = rootRef.value?.offsetHeight || 0;
  };

  onUnmounted(() => {
    window.removeEventListener('resize', updateSizings);
  });

  onMounted(() => {
    window.addEventListener('resize', updateSizings);
    updateSizings();
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    ref="rootRef"
    class="tl-swiper"
    @touchmove="OnTouchMove"
    @touchstart="OnTouchStart"
    @touchend="OnTouchEnd"
    @mouseleave="onMouseLeave"
    v-on="$listeners"
  >
    <div class="tl-swiper__container">
      <div
        :class="[
          'tl-swiper__list',
          {
            'tl-swiper__list_animation': isAnimation,
          },
        ]"
        :style="`transform: translateX(${translateX}px)`"
        data-test-id="list"
      >
        <img
          v-for="(img, index) in images"
          :key="`${img}_${index}`"
          class="tl-swiper__img"
          loading="lazy"
          :src="img"
          :alt="img"
        />
      </div>
    </div>
    <div v-if="images.length > 1" class="tl-swiper__items">
      <div
        v-for="(img, index) in images"
        :key="`item_${img}`"
        class="tl-swiper__item-wrapper"
        @mouseenter="onMouseEnter(index)"
      >
        <div
          :class="[
            'tl-swiper__item',
            {
              'tl-swiper__item_active': index === modelValue,
            },
          ]"
          data-test-id="item"
        ></div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .tl-swiper {
    cursor: pointer;
    display: inline-block;
    height: 100%;
    position: relative;
    touch-action: pan-y;
    width: 100%;

    @media (hover: none) and (pointer: coarse) {
      &__items {
        height: auto;
      }
    }

    &__container {
      border-radius: var(--rounding-m);
      height: 100%;
      overflow-x: hidden;
      width: 100%;
    }

    &__list {
      display: flex;
      height: 100%;
      width: 100%;

      &_animation {
        transition: all 0.3s ease;
      }
    }

    &__img {
      background-color: gray;
      flex-shrink: 0;
      height: 100%;
      object-fit: cover;
      width: 100%;
    }

    &__items {
      bottom: 0;
      box-sizing: border-box;
      display: flex;
      gap: var(--spacing-xxs);
      height: 100%;
      padding: 0 var(--spacing-m);
      position: absolute;
      width: 100%;
    }

    &__item-wrapper {
      align-items: flex-end;
      display: flex;
      flex: 1;
      padding-bottom: var(--spacing-m);
    }

    &__item {
      background-color: var(--color-text-inverse);
      border-radius: var(--rounding-s);
      height: 4px;
      opacity: 0.4;
      width: 100%;

      &_active {
        opacity: 1;
      }
    }
  }
</style>
